import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AnimatePresence, motion } from "framer-motion";
import SquareLoader from "./SquareLoader";

// 1. Create a Motion-enabled MUI Typography component
const MotionTypography = motion(Typography);

function RotatingText({
  text,
  duration = 1500,
  sx = {},
  typographyProps = {},
}) {
  const [index, setIndex] = useState(0);

  // Ensure text is always an array and memoize it
  const textArray = useMemo(() => {
    return Array.isArray(text) ? text : [text || ""];
  }, [text]);

  useEffect(() => {
    // Only rotate if there are multiple messages
    if (textArray.length <= 1) return;
    
    // Start rotation at regular intervals
    const interval = setInterval(() => {
      setIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % textArray.length;
        return nextIndex;
      });
    }, duration);

    return () => {
      clearInterval(interval);
    };
  }, [textArray.length, duration]);

  const currentText = textArray[index] || textArray[0] || "";

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 3, // Space between loader and text
        width: "100%",
        ...sx,
      }}
    >
      {/* Square Loader */}
      <SquareLoader />

      {/* Rotating Text Container */}
      <Box
        sx={{
          overflow: "hidden", // Hides the text sliding in/out
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "40px",
          width: "100%",
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <MotionTypography
            key={`text-${index}-${currentText}`}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={{ 
              duration: 0.4, 
              ease: [0.4, 0, 0.2, 1] // Smooth cubic-bezier easing
            }}
            {...typographyProps}
            component="div"
            sx={{
              width: "100%",
              textAlign: "center",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              ...(typographyProps?.sx || {}),
            }}
          >
            {currentText}
          </MotionTypography>
        </AnimatePresence>
        
        {/* Hidden text to maintain container height */}
        <Typography 
          component="div" 
          sx={{ 
            visibility: "hidden", 
            ...typographyProps,
            width: "100%",
            textAlign: "center",
          }}
        >
          {currentText}
        </Typography>
      </Box>
    </Box>
  );
}

export default RotatingText;